import type { AxiosError } from 'axios';
import axios from 'axios';
import { toast } from 'sonner';
import { config } from '@/shared/config/env';
import { logger } from '@/shared/lib/logger';
import { navigateTo } from '@/shared/lib/navigation';
import { clearUser } from '@/shared/store/auth';
import { ApiError, type ApiErrorResponse } from './errors';

/**
 * 401 에러 처리 debounce 플래그
 * 여러 API 요청이 동시에 401을 받을 때 중복 처리 방지
 */
let isLoggingOut = false;
const LOGOUT_DEBOUNCE_MS = 500;

export const axiosInstance = axios.create({
  baseURL: config.apiBaseUrl,
  timeout: config.apiTimeout,
  withCredentials: true, // 쿠키 기반 세션 인증
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
// 쿠키 기반 세션 인증 사용으로 Authorization 헤더 불필요
// withCredentials: true 설정으로 쿠키가 자동 전송됨

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiErrorResponse>) => {
    const apiError = ApiError.fromAxiosError(error);

    // 에러 유형별 처리
    switch (apiError.status) {
      case 401:
        // 중복 로그아웃 방지: 여러 API가 동시에 401 받을 때 한 번만 처리
        if (!isLoggingOut) {
          isLoggingOut = true;
          logger.info('Session expired, redirecting to login', {
            action: 'auth_logout',
            metadata: { reason: 'session_expired' },
          });
          clearUser();
          navigateTo('/login');
          toast.error('세션이 만료되었습니다. 다시 로그인해주세요.');

          // debounce 해제
          setTimeout(() => {
            isLoggingOut = false;
          }, LOGOUT_DEBOUNCE_MS);
        }
        break;
      case 403:
        toast.error('접근 권한이 없습니다.');
        break;
      case 404:
        toast.error('요청한 리소스를 찾을 수 없습니다.');
        break;
      case 422:
        // 유효성 검증 에러는 폼에서 처리
        break;
      case 500:
      case 502:
      case 503:
        toast.error('서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
        break;
      case 0:
        // 네트워크 에러
        toast.error('네트워크 연결을 확인해주세요.');
        break;
    }

    return Promise.reject(apiError);
  },
);

/**
 * API Client (Axios Instance Alias)
 * @deprecated Use `axiosInstance` for consistency. This alias is for backward compatibility.
 */
export const apiClient = axiosInstance;

/**
 * Orval Mutator Function
 *
 * Orval requires a mutator function that accepts (config, options) and returns a Promise.
 * This wrapper function allows Orval to use our configured axios instance.
 *
 * @see https://orval.dev/reference/configuration/output#mutator
 */
// biome-ignore lint/suspicious/noExplicitAny: Orval mutator signature requires any
export const customInstance = async <T>(requestConfig: any, options?: any): Promise<T> => {
  const source = options?.signal ? axios.CancelToken.source() : undefined;

  if (source && options?.signal) {
    options.signal.addEventListener('abort', () => {
      source.cancel('Query was cancelled');
    });
  }

  const response = await axiosInstance({
    ...requestConfig,
    timeout: config.apiTimeout,
    cancelToken: source?.token,
  });
  return response.data;
};
